import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useAtomValue } from 'jotai';
import { GetStructureDetailsDocument, useUploadCoverImageMutation } from '@/__generated__/graphql';
import { selectedStructure } from '../state';
import { convertToBase64, useSnackBarMessage } from '../utils';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FindInPageOutlined from '@mui/icons-material/FindInPageOutlined';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { textButtonStyle, containedButtonStyle } from '@/theme';

type ImageUploadDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const ImageUploadDialog = ({ open, onClose }: ImageUploadDialogProps) => {
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const structure = useAtomValue(selectedStructure);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [updateCoverImage, { loading: isLoading }] = useUploadCoverImageMutation({
    refetchQueries: [GetStructureDetailsDocument],
  });

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setSelectedFile(file);
    },
    [setSelectedFile]
  );

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [selectedFile]);

  const handleConfirm = useCallback(async () => {
    if (!selectedFile) return;

    try {
      const isImage = selectedFile.type.startsWith('image/');
      const image = isImage ? await convertToBase64(selectedFile) : '';
      updateCoverImage({
        variables: {
          input: {
            structureId: structure?.id ?? '',
            lastModifiedFor: 'Cover Image Updated',
            image: image,
          },
        },
        onCompleted: (response) => {
          onClose();
          if (!response?.uploadCoverImage?.coverImagePath) {
            showSnackBar('An error occurred while uploading platform image', 'error');
            return;
          }
          showSnackBar('Image uploaded successfully', 'success');
        },
        onError: (error) => {
          onClose();
          console.error(error);
          showSnackBar(error.message, 'error');
        },
      });
    } catch (error) {
      onClose();
      console.error('Upload failed', error);
    }
  }, [onClose, selectedFile, showSnackBar, structure?.id, updateCoverImage]);

  const dialogContent = (
    <Stack direction="column" display="flex" flexDirection="column" gap={2}>
      <Button
        variant="contained"
        component="label"
        sx={containedButtonStyle}
        startIcon={<FindInPageOutlined />}
      >
        Choose Image{''}
        <input type="file" accept="image/*" hidden onChange={handleFileChange} />
      </Button>
      {previewUrl && (
        <Box
          component="img"
          src={previewUrl}
          alt="Preview"
          sx={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 1 }}
        />
      )}
    </Stack>
  );

  const dialogActions = (
    <>
      <Button variant="text" onClick={onClose} sx={textButtonStyle}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={() => handleConfirm()}
        sx={containedButtonStyle}
        disabled={!selectedFile || isLoading}
      >
        Upload
      </Button>
    </>
  );

  return (
    <GenericDialog
      open={open}
      onClose={onClose}
      title="Upload an Image"
      content={dialogContent}
      actions={dialogActions}
      maxWidth="sm"
      minWidth="30rem"
      showCloseIcon
      actionsStyles={{ mr: 3, pb: 3 }}
    />
  );
};
